/**
 * LotteryWheel – Canvas Spin-Wheel Engine
 * Standalone module, no dependencies.
 * Usage:
 *   const wheel = new LotteryWheel(canvasElement);
 *   wheel.setPrizes([ { name, color, displayText } ]);
 *   wheel.spin(targetIndex).then(() => { ... });
 */
(function () {
  'use strict';

  class _LotteryWheelCore {
    constructor(canvas, options = {}) {
      this.canvas = canvas;
      this.ctx = canvas.getContext('2d');
      this.prizes = [];
      this.rotation = 0;        // current rotation in radians
      this.isSpinning = false;
      this.dpr = window.devicePixelRatio || 1;

      // Options
      this.outerRingWidth = options.outerRingWidth || 10;
      this.outerRingColor = options.outerRingColor || '#ffb476';
      this.dotColor1 = options.dotColor1 || '#fff';
      this.dotColor2 = options.dotColor2 || '#ffe4d6';
      this.textColor = options.textColor || '#fff';
      this.fontSize = options.fontSize || 13;
      this.centerRadius = options.centerRadius || 30;

      this._images = {}; // Cache for preloaded sector images
      this._resizeObserver = null;
      this._init();
    }

    _init() {
      this._resize();
      // Watch container size changes
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => this._resize());
        this._resizeObserver.observe(this.canvas.parentElement);
      }
    }

    _resize() {
      // canvas is position:absolute filling the wrapper via CSS.
      // Use offsetWidth (CSS-determined) as the authoritative size.
      const size = this.canvas.offsetWidth || this.canvas.parentElement.getBoundingClientRect().width;
      if (!size) return;
      this.canvas.width = size * this.dpr;
      this.canvas.height = size * this.dpr;
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.size = size;
      this._draw();
    }

    /**
     * Set prizes array.
     * @param {Array<{name: string, color: string, displayText?: string}>} prizes
     */
    setPrizes(prizes) {
      this.prizes = prizes || [];
      this._images = {};
      this._draw();

      // Preload images asynchronously; redraw when each loads
      prizes.forEach((p, i) => {
        if (!p.imageUrl) return;
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          this._images[i] = img;
          this._draw();
        };
        img.src = p.imageUrl;
      });
    }

    _draw() {
      const ctx = this.ctx;
      const size = this.size;
      if (!size || this.prizes.length === 0) return;

      const cx = size / 2;
      const cy = size / 2;
      const outerR = size / 2;
      const innerR = outerR - this.outerRingWidth;
      const n = this.prizes.length;
      const sliceAngle = (2 * Math.PI) / n;
      // Scale font proportionally to wheel radius (ref: innerR=158 at desktop max-width:340px canvas)
      const effectiveFontSize = Math.max(8, Math.round(this.fontSize * (innerR / 158)));

      ctx.clearRect(0, 0, size, size);
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(this.rotation);

      // 1) Outer decorative ring
      ctx.beginPath();
      ctx.arc(0, 0, outerR, 0, 2 * Math.PI);
      ctx.fillStyle = this.outerRingColor;
      ctx.fill();

      // Dots on outer ring
      const dotR = 3;
      const dotCount = Math.max(n * 2, 16);
      for (let i = 0; i < dotCount; i++) {
        const angle = (2 * Math.PI * i) / dotCount;
        const dx = (outerR - this.outerRingWidth / 2) * Math.cos(angle);
        const dy = (outerR - this.outerRingWidth / 2) * Math.sin(angle);
        ctx.beginPath();
        ctx.arc(dx, dy, dotR, 0, 2 * Math.PI);
        ctx.fillStyle = i % 2 === 0 ? this.dotColor1 : this.dotColor2;
        ctx.fill();
      }

      // 2) Prize sectors and Figma-aligned content placement
      for (let i = 0; i < n; i++) {
        const startAngle = i * sliceAngle - Math.PI / 2;
        const endAngle = startAngle + sliceAngle;

        // Sector fill
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.arc(0, 0, innerR, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = this.prizes[i].color || this._defaultColor(i, n);
        ctx.fill();

        ctx.save();
        ctx.rotate(startAngle + sliceAngle / 2);

        const img = this._images[i];
        const text = this.prizes[i].displayText || this.prizes[i].name || '';
        const half = sliceAngle / 2;
        const textRadius = innerR * 0.86;
        const textArc = 2 * textRadius * Math.sin(half) * 0.86;

        ctx.save();
        ctx.translate(textRadius, 0);
        ctx.rotate(Math.PI / 2);
        ctx.fillStyle = this.textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const textMetrics = this._drawTangentialText(ctx, text, textArc, effectiveFontSize);
        ctx.restore();

        if (img) {
          const imageHeight = img.naturalHeight || img.height || 1;
          const imageGap = Math.max(4, effectiveFontSize * 0.3);
          const desiredImageRadius = textRadius - textMetrics.totalHeight / 2 - imageGap - imageHeight / 2;
          const imageRadius = Math.min(innerR * 0.58, Math.max(innerR * 0.28, desiredImageRadius));
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(0, 0);
          ctx.arc(0, 0, innerR, -half, half);
          ctx.closePath();
          ctx.clip();
          ctx.translate(imageRadius, 0);
          ctx.rotate(Math.PI / 2);
          this._drawImageNatural(ctx, img);
          ctx.restore();
        }

        ctx.restore();
      }

      ctx.restore();
    }

    /**
     * Draw text tangentially within a sector at fixed font size.
     * Wraps to multiple lines when text overflows maxWidth.
     * Call after: translate(textRadius, 0) + rotate(+π/2).
     */
    _drawTangentialText(ctx, text, maxWidth, baseFontSize) {
      const lineH = baseFontSize * (12 / 14);
      ctx.font = `600 ${baseFontSize}px "FTTerra", "FT Terra", sans-serif`;

      // Single line fits — draw and return
      if (ctx.measureText(text).width <= maxWidth) {
        ctx.fillText(text, 0, 0);
        return { totalHeight: lineH };
      }

      // Greedy word-wrap (handles space-separated languages)
      const words = text.split(/\s+/);
      const lines = [];
      let current = '';
      for (const word of words) {
        const candidate = current ? current + ' ' + word : word;
        if (ctx.measureText(candidate).width <= maxWidth) {
          current = candidate;
        } else {
          if (current) lines.push(current);
          current = word;
        }
      }
      if (current) lines.push(current);

      // Single long word (e.g. Japanese) — split at character midpoint
      if (lines.length === 1 && ctx.measureText(lines[0]).width > maxWidth) {
        const s = lines[0];
        const mid = Math.ceil(s.length / 2);
        lines[0] = s.slice(0, mid);
        lines.push(s.slice(mid));
      }

      // Draw lines centered vertically around origin
      const totalH = (lines.length - 1) * lineH;
      lines.forEach((line, idx) => {
        ctx.fillText(line, 0, -totalH / 2 + idx * lineH);
      });
      return { totalHeight: lines.length * lineH };
    }

    _drawImageNatural(ctx, image) {
      const width = image.naturalWidth || image.width;
      const height = image.naturalHeight || image.height;
      if (!width || !height) return;
      ctx.drawImage(image, -width / 2, -height / 2, width, height);
    }

    _defaultColor(i, n) {
      const colors = [
        '#fff8ec', '#ffe4c2', '#fff8ec', '#ffe4c2', '#fff8ec',
        '#ffe4c2', '#fff8ec', '#ffe4c2', '#fff8ec', '#ffe4c2',
        '#fff8ec', '#ffe4c2', '#fff8ec', '#ffe4c2', '#fff8ec',
      ];
      return colors[i % colors.length];
    }

    /**
     * Spin the wheel to land on targetIndex.
     * @param {number} targetIndex – index in prizes array
     * @returns {Promise<void>} resolves when spin completes
     */
    spin(targetIndex) {
      if (this.isSpinning) return Promise.reject(new Error('Already spinning'));
      if (targetIndex < 0 || targetIndex >= this.prizes.length) {
        return Promise.reject(new Error('Invalid target index'));
      }

      this.isSpinning = true;

      return new Promise((resolve) => {
        const n = this.prizes.length;
        const sliceAngle = (2 * Math.PI) / n;

        // Target angle: slice CENTER pointing up (top = pointer position)
        // Sector i starts at: i * sliceAngle - π/2, center at: (i + 0.5) * sliceAngle - π/2
        // After applying rotation R, screen angle = (i + 0.5) * sliceAngle - π/2 + R
        // We need screen angle = -π/2 (top), so R = -(i + 0.5) * sliceAngle
        const targetAngle = -((targetIndex + 0.5) * sliceAngle);

        // Add extra full rotations for visual effect
        const extraRotations = (5 + Math.floor(Math.random() * 3)) * 2 * Math.PI;
        // Small random offset within ±30% of slice to look natural
        const randomOffset = (Math.random() - 0.5) * sliceAngle * 0.5;

        const startRotation = this.rotation;
        const endRotation = targetAngle + extraRotations + randomOffset;
        // Normalize so we always spin forward
        const totalDelta = endRotation - startRotation;

        const duration = 4000 + Math.random() * 1500; // 4-5.5 seconds
        const startTime = performance.now();

        const animate = (now) => {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / duration, 1);
          // Ease-out cubic for natural deceleration
          const ease = 1 - Math.pow(1 - t, 3);

          this.rotation = startRotation + totalDelta * ease;
          this._draw();

          if (t < 1) {
            requestAnimationFrame(animate);
          } else {
            // Normalize rotation to [0, 2π)
            this.rotation = this.rotation % (2 * Math.PI);
            this.isSpinning = false;
            resolve();
          }
        };

        requestAnimationFrame(animate);
      });
    }

    destroy() {
      if (this._resizeObserver) {
        this._resizeObserver.disconnect();
        this._resizeObserver = null;
      }
    }
  }

  // Custom Element wrapper — satisfies the "class extends HTMLElement" convention
  class LotteryWheel extends HTMLElement {
    connectedCallback() {
      const canvas = this.querySelector('.lottery-wheel-canvas');
      if (!canvas) return;
      this._core = new _LotteryWheelCore(canvas);
    }

    disconnectedCallback() {
      if (this._core) this._core.destroy();
    }

    /** Apply wheel appearance options (called by lottery-popup after DOM is ready). */
    setOptions(opts) {
      if (!this._core) { this._pendingOpts = opts; return; }
      if (opts.outerRingColor !== undefined) this._core.outerRingColor = opts.outerRingColor;
      if (opts.dotColor1      !== undefined) this._core.dotColor1      = opts.dotColor1;
      if (opts.dotColor2      !== undefined) this._core.dotColor2      = opts.dotColor2;
      if (opts.textColor      !== undefined) this._core.textColor      = opts.textColor;
      if (opts.fontSize       !== undefined) this._core.fontSize       = parseFloat(opts.fontSize);
      if (opts.outerRingWidth !== undefined) this._core.outerRingWidth = parseFloat(opts.outerRingWidth);
      this._core._draw();
    }

    setPrizes(prizes)     { return this._core?.setPrizes(prizes); }
    spin(targetIndex)     { return this._core?.spin(targetIndex); }
    get isSpinning()      { return this._core?.isSpinning || false; }
  }

  if (!customElements.get('lottery-wheel')) {
    customElements.define('lottery-wheel', LotteryWheel);
  }

  // Export
  window.LotteryWheel = LotteryWheel;
})();
